import React, { useState, useEffect, useRef } from "react";
import { FiSearch, FiX, FiCheck } from "react-icons/fi";
import { useComponentTranslation } from "@/services/i18n";

const translations = {
  en: {
    clear_selection: "Clear selection",
    no_results_found: "No results found",
  },
  fr: {
    clear_selection: "Effacer la sélection",
    no_results_found: "Aucun résultat trouvé",
  },
};

export default function FilterDropdown({ icon, label, options = [], value = [], onChange, multiSelect = true, placeholder, onClear, className = "" }) {
  const { t } = useComponentTranslation(translations);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
  const selectedCount = selectedValues.length;
  const hasSelection = selectedCount > 0;

  useEffect(() => {
    if (searchTerm?.trim() === "") {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) => {
        const optionLabel = typeof option === "object" ? option.label : option;
        return optionLabel?.toString().toLowerCase().includes(searchTerm?.toLowerCase());
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const handleEscape = (event) => {
    if (event.key === "Escape") {
      setIsOpen(false);
      buttonRef.current?.focus();
    }
  };

  const handleToggleOption = (optionValue) => {
    if (multiSelect) {
      const newValues = selectedValues.includes(optionValue) ? selectedValues.filter((v) => v !== optionValue) : [...selectedValues, optionValue];
      onChange(newValues);
    } else {
      onChange(selectedValues.includes(optionValue) ? [] : [optionValue]);
      setIsOpen(false);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange([]);
    setSearchTerm("");
    if (onClear) onClear();
  };

  const getOptionLabel = (option) => {
    return typeof option === "object" ? option.label : option;
  };

  const getOptionValue = (option) => {
    return typeof option === "object" ? option.value : option;
  };

  const searchPlaceholder = placeholder || `${label}...`;

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 p-2 rounded-lg text-sm font-medium text-gray-700 border ${
          hasSelection ? "bg-white hover:bg-gray-50" : "border-dashed bg-gray-50 hover:bg-gray-100"
        } border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary ${isOpen ? "bg-gray-50" : ""}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {icon && <span className="text-gray-600">{icon}</span>}
        <span>{label}</span>
        {hasSelection && (
          <>
            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-xs font-semibold text-gray-700 border border-gray-300">{selectedCount}</span>

            <span
              onClick={handleClear}
              className="ml-1 p-1 hover:bg-gray-200 rounded-full transition-colors text-sm"
              aria-label={t("clear_selection")}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <FiX className="text-gray-500" />
            </span>
          </>
        )}
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute left-0 mt-2 w-64 bg-white rounded-xl border border-gray-200 shadow-xl py-2 z-50 focus:outline-none animate-in fade-in slide-in-from-top-2 duration-200"
        >
          {/* Search Input */}
          <div className="px-3 pb-2 border-b border-gray-200">
            <div className="relative">
              <FiSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                className="w-full pl-9 pr-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-auto py-1">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">{t("no_results_found")}</div>
            ) : (
              filteredOptions.map((option, index) => {
                const optionValue = getOptionValue(option);
                const optionLabel = getOptionLabel(option);
                const isSelected = selectedValues.includes(optionValue);

                return (
                  <button
                    key={optionValue || index}
                    type="button"
                    onClick={() => handleToggleOption(optionValue)}
                    onMouseEnter={(e) => {
                      e.currentTarget.classList.add("bg-gray-50");
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.classList.remove("bg-gray-50");
                    }}
                    className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-gray-900 hover:bg-gray-50"
                  >
                    {/* Checkbox */}
                    {multiSelect && (
                      <div
                        className={`flex items-center justify-center w-5 h-5 rounded border transition-all ${
                          isSelected ? "bg-primary border-primary" : "bg-white border-gray-300"
                        }`}
                      >
                        {isSelected && <FiCheck className="w-4 h-4 text-white" />}
                      </div>
                    )}

                    <span className={`flex-1 ${isSelected ? "font-medium" : "font-normal"}`}>{optionLabel}</span>
                    {!multiSelect && isSelected && <FiCheck className="w-4 h-4 text-primary" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
